/**
 * Ad Ops API client — talks to /api/ad-ops/*.
 *
 * Mirrors the adminApi.ts pattern (thin wrappers around apiRequest).
 */

import { apiRequest } from './api';

const AD_OPS_BUNDLES_BASE = '/api/ad-ops/bundles';

export interface AdOpsStatus {
  canViewAdOps: boolean;
  dryRun: boolean;
  /**
   * Whether the current user has `admin.view`. Controls visibility of the
   * Phase 0 diagnostics drawer — Phase 0 is a developer/admin tool, not part
   * of the Ad Ops day-to-day flow.
   */
  isAdmin: boolean;
}


export interface BundlePreviewVideo {
  contentId: string;
  title: string;
  durationSeconds?: number;
  matchedKeywords: string[];
  /** Resolved from GAM `Content.cmsSources` when one entry is a YouTube ID. */
  youtubeVideoId?: string;
}

export interface BundlePreviewResponse {
  expandedKeywords: string[];
  matchedKeywords: string[];
  droppedKeywords: string[];
  matchedContentCount: number;
  sampleVideos: BundlePreviewVideo[];
  suggestedBundleName: string;
  truncated: boolean;
}

export interface BundleCreateResponse {
  bundle: {
    id: string;
    name: string;
    description?: string;
    contentIds: string[];
  };
  dryRun: boolean;
  gamUrl?: string;
  /**
   * Full list of content IDs the user picked. Echoed back so the success
   * card can offer a "Copy IDs to clipboard" handoff — v202605 ContentBundle
   * SOAP type has no membership fields, so the bundle is created empty in
   * GAM and the user pastes these into the bundle's Content tab.
   */
  suggestedContentIds: string[];
  /** Matched keywords from the originating preview, for the success card. */
  matchedKeywords: string[];
}

export type BundleAuditStatus = 'attempting' | 'created' | 'failed';

export interface BundleAuditLogEntry {
  id: string;
  bundleId?: string;
  bundleName: string;
  topic: string;
  expandedKeywords: string[];
  matchedKeywords: string[];
  /** New-shape full list of suggested content IDs (post-v2 entries). */
  suggestedContentIds?: string[];
  /** Legacy field on older entries. */
  contentIdCount?: number;
  status?: BundleAuditStatus;
  errorMessage?: string;
  createdBy: string;
  createdAt: number;
  updatedAt?: number;
  dryRun: boolean;
}

/** Confirm the current user can view the Ad Ops dashboard. */
export const getAdOpsStatus = async (): Promise<AdOpsStatus> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/status`);

/**
 * Anchor-sample cache summary. Pass `full: true` to also get the per-scope
 * sample arrays (useful for debugging only — the LLM prompts use them
 * server-side, no frontend rendering needed).
 */
export const getKeywords = async (
  options: { full?: boolean } = {},
): Promise<{
  keyIds: Partial<Record<MetadataScope, string>>;
  fetchedAt: number;
  count: number;
  countsByScope?: Partial<Record<MetadataScope, number>>;
  anchorSamples?: Partial<Record<MetadataScope, string[]>>;
}> =>
  apiRequest(
    `${AD_OPS_BUNDLES_BASE}/keywords${options.full ? '?full=1' : ''}`,
  );

export type RefreshState = 'idle' | 'refreshing' | 'error';

/** GAM CmsMetadataKey names the cache covers. Mirrors `METADATA_SCOPES` on the backend. */
export type MetadataScope = 'keywords' | 'category' | 'yt_channel_id';

export interface RefreshStatus {
  state: RefreshState;
  startedAt: number | null;
  completedAt: number | null;
  fetchedAt: number | null;
  /** Total values across all scopes. */
  count: number;
  /** Per-scope counts. Omitted when no cache has loaded yet. */
  countsByScope?: Partial<Record<MetadataScope, number>>;
  error: string | null;
}

/**
 * Kick off a background keyword cache refresh on the server. Returns
 * immediately with the current job status (the actual SOAP fetch runs
 * detached). Poll `getRefreshStatus()` until `state === 'idle'`.
 */
export const refreshKeywords = async (): Promise<RefreshStatus> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/keywords/refresh`, { method: 'POST' });

/** Current refresh-job status snapshot — cheap, safe to poll. */
export const getRefreshStatus = async (): Promise<RefreshStatus> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/keywords/refresh/status`);

/** Thrown by previewBundle / matchKeyword when the server returns 409 CACHE_COLD. */
export class CacheColdError extends Error {
  readonly refreshStatus: RefreshStatus;
  constructor(refreshStatus: RefreshStatus, message?: string) {
    super(message ?? 'Keyword cache is cold; refresh first.');
    this.name = 'CacheColdError';
    this.refreshStatus = refreshStatus;
  }
}

/**
 * Wrap a server call that may return 409 CACHE_COLD with the conversion to
 * `CacheColdError`. Both /preview and /match-keyword use this convention, so
 * sharing the rethrow keeps them in lockstep.
 */
const rethrowCacheCold = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (err: any) {
    if (err?.status !== 409) throw err;
    const status = await getRefreshStatus().catch(() => null);
    throw new CacheColdError(
      status ?? {
        state: 'refreshing',
        startedAt: Date.now(),
        completedAt: null,
        fetchedAt: null,
        count: 0,
        error: null,
      },
      err?.message,
    );
  }
};

/** Topic → expanded keywords + matched videos preview. */
export const previewBundle = (input: {
  topic: string;
  additionalKeywords?: string[];
  targetMarkets?: string[];
}): Promise<BundlePreviewResponse> =>
  rethrowCacheCold(() =>
    apiRequest(`${AD_OPS_BUNDLES_BASE}/preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );

/** Create the bundle in GAM (or dry-run, depending on server flag). */
export const createBundle = async (input: {
  name: string;
  contentIds: string[];
  description?: string;
  source?: {
    topic: string;
    expandedKeywords: string[];
    matchedKeywords: string[];
  };
}): Promise<BundleCreateResponse> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/create`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/** Recent bundle create activity. */
export const getAuditLog = async (limit = 50): Promise<{ entries: BundleAuditLogEntry[] }> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/audit-log?limit=${limit}`);

export interface KeywordMatchResult {
  query: string;
  matched: {
    gamKeyword: string;
    valueId: string;
    score: number;
    /** Which GAM CmsMetadataKey the match came from. Older builds omit this. */
    scope?: MetadataScope;
  } | null;
}

/**
 * Look up a freeform candidate against the cached GAM keyword universe.
 * Powers the chip-add input — user types a term, gets back the GAM keyword
 * (with confidence score) or `null` if it doesn't exist in their network.
 * Throws CacheColdError on a 409 so the caller can reuse the refresh-banner
 * flow.
 */
export const matchKeyword = (term: string): Promise<KeywordMatchResult> =>
  rethrowCacheCold(() =>
    apiRequest(`${AD_OPS_BUNDLES_BASE}/match-keyword?term=${encodeURIComponent(term)}`),
  );

export type DiagnosticStatus = 'pass' | 'fail' | 'warn' | 'skipped';

export interface DiagnosticCheck {
  id: string;
  label: string;
  status: DiagnosticStatus;
  details: string;
  payloadPreview?: any;
}

export interface Phase0DiagnosticsResult {
  bundleId: string;
  overall: 'pass' | 'fail';
  checks: DiagnosticCheck[];
  samples: {
    bundleContentIdField?: string;
    sampledContentId?: string;
    sampledContentCmsMetadataValueIds?: string[];
  };
}

/**
 * Run read-only Phase 0 SOAP contract checks against an EXISTING bundle.
 * Nothing is written to GAM. Admin-only on the server.
 *
 * Aborts after `timeoutMs` (default 25s) so the UI fails fast instead of
 * hanging on a slow SOAP call — Heroku itself caps requests at 30s, and the
 * server-side run isn't restartable from here either way.
 */
export const runDiagnostics = async (
  bundleId: string,
  options: { timeoutMs?: number } = {},
): Promise<Phase0DiagnosticsResult> => {
  const timeoutMs = options.timeoutMs ?? 25_000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await apiRequest(
      `${AD_OPS_BUNDLES_BASE}/diagnose?bundleId=${encodeURIComponent(bundleId)}`,
      { signal: controller.signal },
    );
  } catch (err: any) {
    if (err?.name === 'AbortError' || controller.signal.aborted) {
      throw new Error(
        `Diagnostics timed out after ${Math.round(timeoutMs / 1000)}s. ` +
          'Phase 0 is a developer/admin tool — if it keeps timing out, ' +
          'run it from a backend shell instead of the UI.',
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

export interface PqlFilterProbeAttempt {
  label: string;
  query: string;
  success: boolean;
  errorMessage?: string;
  totalResultSetSize?: number;
  rowCount?: number;
  firstRowKeys?: string[];
  firstRowSample?: any;
}

export interface PqlFilterProbeResult {
  keywordsKeyId: string | null;
  attempts: PqlFilterProbeAttempt[];
}

/**
 * One-shot probe: does GAM accept `WHERE name LIKE` on the metadata-values
 * endpoint? Result tells us whether we can ditch the 1-3 minute bulk paginate
 * in favour of per-candidate search-on-demand. Admin-only on the server.
 */
export const testPqlFilter = (term: string): Promise<PqlFilterProbeResult> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/test-pql-filter?term=${encodeURIComponent(term)}`);

export interface ContentPqlProbeAttempt {
  label: string;
  query: string;
  success: boolean;
  errorMessage?: string;
  totalResultSetSize?: number;
  rowCount?: number;
  firstRowKeys?: string[];
}

export interface ContentPqlProbeResult {
  sampleValueId: string | null;
  attempts: ContentPqlProbeAttempt[];
}

/**
 * Probe ContentService PQL syntax. Forces a column-list error and tries
 * several membership operators against a known-good value id, so we can
 * see which form GAM actually accepts. Admin-only on the server.
 */
export const testContentFilter = (): Promise<ContentPqlProbeResult> =>
  apiRequest(`${AD_OPS_BUNDLES_BASE}/test-content-filter`);
